/**
 * Course Campaign Service
 * 
 * Manages the link between a user's campaign hub and a specific course.
 * Each user gets one campaign per course PER TEMPLATE, auto-created on first click
 * using the template the admin selected.
 */

import { supabase } from '@/integrations/supabase/client';
import { createCampaignHub, getCampaignHubById, type CampaignHub, type HubTemplate } from './campaignHubService';
import { authHeaders } from '@/lib/authHeaders';
import type { CampaignManifest } from '@/types/campaignIntegration';

export interface UserCourseCampaign {
  id: string;
  user_id: string;
  course_id: string;
  campaign_hub_id: string;
  template: string;
  created_at: string;
}

export async function listCourseCampaignManifests(courseId: string, signal?: AbortSignal): Promise<CampaignManifest[]> {
  const response = await fetch(`/api/courses/${encodeURIComponent(courseId)}/campaigns`, { headers: await authHeaders(), signal });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'Failed to list course campaigns');
  return Array.isArray(body?.campaigns) ? body.campaigns as CampaignManifest[] : [];
}

export async function materializeCampaignDefinition(definitionId: string): Promise<CampaignManifest> {
  const response = await fetch(`/api/campaign-definitions/${encodeURIComponent(definitionId)}/materialize`, { method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'Failed to start course campaign');
  return body as CampaignManifest;
}

/**
 * Get the user's existing campaign for a specific course + template.
 * Returns the newest campaign hub matching the admin's current template.
 */
export async function getUserCourseCampaign(
  userId: string,
  courseId: string,
  template: HubTemplate
): Promise<CampaignHub | null> {
  // Look up the junction row filtered by template, newest first
  const { data, error } = await supabase
    .from('user_course_campaigns')
    .select('campaign_hub_id')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .eq('template', template)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('getUserCourseCampaign error:', error.message);
    return null;
  }

  if (!data) return null;

  // Fetch the full campaign hub
  const hub = await getCampaignHubById(data.campaign_hub_id);

  if (!hub) {
    // Hub may have been deleted — clean up the orphaned link.
    await supabase.from('user_course_campaigns').delete().eq('campaign_hub_id', data.campaign_hub_id);
    return null;
  }

  return hub as CampaignHub;
}

/**
 * Create a new campaign for the user in a specific course.
 * Uses the admin-configured template for that course.
 */
export async function createCourseCampaign(
  userId: string,
  courseId: string,
  courseTitle: string,
  template: HubTemplate
): Promise<{ hub: CampaignHub | null; error: string | null }> {
  // Create the campaign hub
  const campaignTitle = `${courseTitle} Campaign`;
  const { hub, error } = await createCampaignHub(userId, campaignTitle, template);

  if (error || !hub) {
    return { hub: null, error: error || 'Failed to create campaign hub' };
  }

  // Link it to the course with the template
  const { error: linkError } = await supabase
    .from('user_course_campaigns')
    .insert({
      user_id: userId,
      course_id: courseId,
      campaign_hub_id: hub.id,
      template: template,
    });

  if (linkError) {
    console.error('createCourseCampaign link error:', linkError.message);

    // If it's a unique constraint violation, the user already has a campaign for this template
    if (linkError.code === '23505') {
      const existing = await getUserCourseCampaign(userId, courseId, template);
      return { hub: existing, error: null };
    }

    return { hub: null, error: linkError.message };
  }

  return { hub, error: null };
}

/**
 * Get or create the user's campaign for a course.
 * This is the main entry point: returns existing if found (for the current template),
 * creates new otherwise.
 */
export async function getOrCreateCourseCampaign(
  userId: string,
  courseId: string,
  courseTitle: string,
  template: HubTemplate
): Promise<{ hub: CampaignHub | null; error: string | null }> {
  // First check if there is ANY campaign hub linked to this user for this course, regardless of template.
  const { data: junction } = await supabase
    .from('user_course_campaigns')
    .select('campaign_hub_id, template')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (junction) {
    if (junction.template !== template) {
      // A template/definition change must never overwrite learner-owned
      // campaign JSON. The old junction remains pinned and a separate hub is
      // materialized for the newly selected template.
      return createCourseCampaign(userId, courseId, courseTitle, template);
    }

    // Fetch the updated campaign hub to return it
    const { data: hub, error: hubError } = await supabase
      .from('campaign_hubs')
      .select('*')
      .eq('id', junction.campaign_hub_id)
      .single();

    if (hub && !hubError) {
      return { hub: hub as CampaignHub, error: null };
    }
  }

  // Fallback: Create new campaign if the user never had one for this course at all
  return createCourseCampaign(userId, courseId, courseTitle, template);
}
